import { Box, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { LLMResponse } from "../../../api/types/chatTypes";
import { useConversation } from "../../../contexts/conversationContext";
import GeneralMarkdown from "../../Common/GeneralMarkdown";
import ShinyText from "../../Common/ShinyText";
import { AnswerLoading } from "./AnswerLoading";

type AnswerContentProps = {
    answer: LLMResponse;
    parsedAnswer: string;
};

/**
 * AnswerContent Component
 *
 * A component that displays formatted Markdown content within a styled container.
 * It supports GitHub Flavored Markdown (GFM) and allows raw HTML rendering.
 *
 * @component
 * @param {AnswerContentProps} props - Component props
 * @returns {JSX.Element} A styled box containing rendered Markdown content.
 *
 * @example
 * ```tsx
 * <AnswerContent content={"**Hello, world!**\n\n- Item 1\n- Item 2"} />
 * ```
 */
function AnswerContent({ answer, parsedAnswer }: AnswerContentProps) {
    const theme = useTheme();

    const { t } = useTranslation("translation", { keyPrefix: "chat" });

    const { embeddingTime, aiSearchTime, foundSources } = useConversation();

    const isLoading =
        answer.responseStatus === "queued" || answer.responseStatus === "loading" || answer.responseStatus === undefined || parsedAnswer.trim() === "";

    return (
        <Box
            sx={{
                color: theme.palette.text.primary,
                fontSize: "1rem",
                fontWeight: "400",
                lineHeight: "1rem",
                paddingBlock: "8px",
                wordBreak: "break-word"
            }}
        >
            {isLoading && (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        gap: 2,
                        mb: 2,
                        py: 1,
                        px: 1.5,
                        borderRadius: 1,
                        backgroundColor: "transparent",
                        transition: "all 0.25s ease",
                        overflow: "auto"
                    }}
                >
                    {!embeddingTime || !aiSearchTime ? (
                        <ShinyText
                            text={t("answerLoading.loadingAnswer")}
                            color={theme.palette.text.disabled}
                            shineColor={theme.palette.text.primary}
                            speed={4}
                        />
                    ) : (
                        <AnswerLoading />
                    )}

                    {answer.toolCallStatus === "started" && (
                        <Typography
                            sx={{
                                fontSize: "0.95rem",
                                fontWeight: 500,
                                opacity: 0.9
                            }}
                        >
                            {embeddingTime && aiSearchTime && t("answerLoading.sourcesFound", { count: foundSources })}
                        </Typography>
                    )}
                </Box>
            )}

            <GeneralMarkdown answer={parsedAnswer} />
        </Box>
    );
}

export default AnswerContent;
